/**
 * ExportReviewModal — IMPL-002 (Layer 4 export review gate, frontend).
 *
 * The mandatory review step between the export menu and the signed URL.
 * Mirrors the DeadlineCalendarExportModal pattern (the reference Layer 4
 * implementation): inline banners for every failure mode, inputs disabled
 * while submitting, the modal never closes until the happy path completes.
 *
 * Scope correction (2026-06-12): this is a mandatory ACKNOWLEDGMENT, not
 * an attestation. There is no affirmation checkbox — the export button is
 * the single deliberate acknowledgment action ("Export"), and clicking it
 * constructs the server-verified acknowledgment payload. Legal-weight
 * framing is reserved for patient-authorization flows. Copy is calm and
 * plain (no em-dashes), versioned via EXPORT_ACK_COPY_VERSION.
 *
 * Two acknowledgment tiers, identical confirm action:
 *  - standard: the document has a citation report with at least one citation.
 *  - elevated_null_citations: the letter is not backed by citations —
 *    citationReport is nullish OR present-but-empty (totalCount === 0; BC-4)
 *    — a calm warning notice ("This draft doesn't have a citation report…")
 *    above the same single confirm. The tier is still server-computed and
 *    still audited as ackType "elevated_null_citations" (ACL weak-data
 *    flagging); the frozen audit value string is unchanged.
 *
 * The tier rendered here is only a prediction for UX; the SERVER computes
 * the required tier from Firestore state and rejects mismatches. On a 403
 * ELEVATED_ACK_REQUIRED / ACK_REQUIRED denial the modal re-renders in the
 * server-required tier, so the recorded ack always matches the notice the
 * user actually saw.
 *
 * Gate denials surfaced (tasks/IMPL-002.md §2.4):
 *  - 403 ACK_REQUIRED / ELEVATED_ACK_REQUIRED → updated notice, export again
 *  - 409 DOC_NOT_EXPORTABLE → "still generating" banner
 *  - 409 DOCUMENT_CHANGED → blocked for this open; parent refreshes
 *  - 503 AUDIT_UNAVAILABLE → retryable error; nothing left the system
 */

import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  X,
} from 'lucide-react';
import { casemakerService } from '../../services/casemakerService';
import {
  GeneratedDocument,
  CitationReport,
  ExportFormat,
  ExportArtifact,
  ExportAckType,
  ExportAcknowledgment,
  getExportGateDenial,
  toExportDocVersionRef,
} from '../../types/generatedDoc';
import {
  EXPORT_ACK_COPY_VERSION,
  DISCLOSURE_COPY,
  CONFIRM_BUTTON_LABEL,
  ELEVATED_WARNING_HEADING,
  ELEVATED_WARNING_BODY,
  BANNER_AUDIT_UNAVAILABLE,
  BANNER_RECONFIRM,
  BANNER_DOC_GENERATING,
  BANNER_DOCUMENT_CHANGED,
  BANNER_VERSION_UNAVAILABLE,
  BANNER_EXPORT_FAILED,
  SUCCESS_BANNER_TEXT,
} from './exportReviewCopy';

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

export interface ExportReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The document being exported. Modal renders nothing when null. */
  document: GeneratedDocument | null;
  format: ExportFormat;
  artifact: ExportArtifact;
  onExported: (result: { exportId?: string; warning?: string }) => void;
  /**
   * PR-3 (finding 1): fired when the server denies with DOCUMENT_CHANGED —
   * the document was modified after this modal's snapshot. The parent
   * should refresh its copy so a re-open reviews the latest version.
   */
  onDocumentChanged?: () => void;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const SUCCESS_BANNER_AUTO_DISMISS_MS = 1800;

/**
 * Is this letter ungrounded, judged from the citation report in hand — mirror
 * of the server's reportIsUngrounded (exportGeneratedDoc). Derived from report
 * composition, NOT a persisted flag, so legacy docs without a `degraded` field
 * predict correctly: a legacy all-unsupported doc (report present,
 * verifiedCount === 0, totalCount > 0) elevates; a legacy grounded doc
 * (verifiedCount > 0) stays standard. Falls back to totalCount/unsupportedCount
 * only if a very old summary lacks verifiedCount.
 */
function reportIsUngrounded(
  report: CitationReport | null | undefined,
): boolean {
  if (report == null) return true;
  const summary = report.summary;
  if (summary == null) return true;
  if (summary.totalCount === 0) return true;
  if (typeof summary.verifiedCount === 'number') {
    return summary.verifiedCount === 0;
  }
  if (typeof summary.unsupportedCount === 'number') {
    return summary.unsupportedCount >= summary.totalCount;
  }
  return false;
}

/**
 * Client-side prediction of the acknowledgment tier. Must stay in lockstep
 * with the server's computeRequiredAckType (exportGeneratedDoc) — if the
 * client predicts standard while the server requires elevated, the export is
 * denied with ELEVATED_ACK_REQUIRED. The elevated tier covers every letter
 * not backed by usable grounding, judged from the report composition
 * (reportIsUngrounded: null / totalCount === 0 / verifiedCount === 0), with the
 * persisted `degraded === true` flag as a secondary signal for newly generated
 * docs. Strict `=== true` so a legacy doc lacking the field is judged purely on
 * report composition.
 */
function predictAckTier(
  document: GeneratedDocument | null,
  artifact: ExportArtifact,
): ExportAckType {
  const ungrounded =
    document?.degraded === true || reportIsUngrounded(document?.citationReport);
  if (ungrounded && artifact !== 'citations') {
    return 'elevated_null_citations';
  }
  return 'standard';
}

function artifactLabel(artifact: ExportArtifact): string {
  switch (artifact) {
    case 'citations':
      return 'Source Citations';
    case 'bundle':
      return 'Letter + Citations bundle';
    default:
      return 'Letter';
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

type BannerKind = 'error' | 'reconfirm' | 'success';

export function ExportReviewModal({
  isOpen,
  onClose,
  document,
  format,
  artifact,
  onExported,
  onDocumentChanged,
}: ExportReviewModalProps): JSX.Element | null {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [banner, setBanner] = useState<{ kind: BannerKind; text: string } | null>(
    null,
  );
  // Server-required tier override — set when the gate rejects our prediction
  // (tier race: citations appeared/disappeared between open and submit).
  const [serverRequiredTier, setServerRequiredTier] =
    useState<ExportAckType | null>(null);
  // PR-3 (finding 1): once the server says DOCUMENT_CHANGED, this snapshot
  // is stale — export stays blocked until the modal is re-opened against a
  // refreshed document.
  const [docChanged, setDocChanged] = useState(false);

  // PR-3 (finding 2): submit-sequence guard, mirroring the Watchtower
  // modal's fetchSeqRef. Closing bumps the sequence; any in-flight submit
  // sees the mismatch after its await and applies no state or side effects
  // (no hidden window.open, no onExported, no tier/banner bleed into the
  // next open). The component stays mounted when closed, so refs persist.
  const submitSeqRef = useRef(0);

  // Reset transient state when the modal closes so a re-open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      submitSeqRef.current++;
      setIsSubmitting(false);
      setBanner(null);
      setServerRequiredTier(null);
      setDocChanged(false);
    }
  }, [isOpen]);

  if (!isOpen || !document) return null;

  const effectiveTier: ExportAckType =
    serverRequiredTier ?? predictAckTier(document, artifact);
  const isElevated = effectiveTier === 'elevated_null_citations';

  const report = document.citationReport;
  const contentVersionLabel = document.hasBeenEdited
    ? 'Your edited version'
    : 'AI-generated version';

  const exportDisabled = isSubmitting || docChanged;
  const dialogId = `export-review-${document.docId}`;

  async function handleExport(): Promise<void> {
    if (exportDisabled || !document) return;
    setBanner(null);

    // PR-3 (finding 1): bind the acknowledgment to the document version in
    // this modal's snapshot — the version the user actually reviewed.
    const docVersion = toExportDocVersionRef(document.updatedAt);
    if (!docVersion) {
      // Defensive: the wire shape should always yield an integer pair. If
      // it doesn't, refresh rather than exporting unbound.
      setBanner({ kind: 'error', text: BANNER_VERSION_UNAVAILABLE });
      onDocumentChanged?.();
      return;
    }

    setIsSubmitting(true);
    const seq = submitSeqRef.current;

    const acknowledgment: ExportAcknowledgment = {
      acknowledged: true,
      ackType: effectiveTier,
      ackCopyVersion: EXPORT_ACK_COPY_VERSION,
      acknowledgedAt: new Date().toISOString(),
      docUpdatedAt: docVersion,
    };

    try {
      const response = await casemakerService.exportDocument({
        docId: document.docId,
        format,
        useEditedContent: true,
        artifact,
        acknowledgment,
      });
      if (seq !== submitSeqRef.current) return; // superseded by close

      if (response.success && response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
        setIsSubmitting(false);
        setBanner({ kind: 'success', text: SUCCESS_BANNER_TEXT });
        onExported({ exportId: response.exportId, warning: response.warning });
        setTimeout(() => {
          if (seq === submitSeqRef.current) onClose();
        }, SUCCESS_BANNER_AUTO_DISMISS_MS);
        return;
      }

      // success=false without a thrown error — treat as a generic failure.
      setIsSubmitting(false);
      setBanner({ kind: 'error', text: response.error || BANNER_EXPORT_FAILED });
    } catch (err) {
      if (seq !== submitSeqRef.current) return; // superseded by close
      setIsSubmitting(false);
      const denial = getExportGateDenial(err);

      if (denial?.code === 'ELEVATED_ACK_REQUIRED' || denial?.code === 'ACK_REQUIRED') {
        // The server's tier is authoritative — re-render the modal in the
        // required tier so the notice on screen matches what the next
        // export click will be recorded against.
        if (denial.requiredAckType) {
          setServerRequiredTier(denial.requiredAckType);
        }
        setBanner({ kind: 'reconfirm', text: BANNER_RECONFIRM });
        return;
      }
      if (denial?.code === 'DOCUMENT_CHANGED') {
        // PR-3 (finding 1): this snapshot no longer matches the stored
        // document. Block export for this open and let the parent refresh
        // so a re-open reviews the latest version.
        setDocChanged(true);
        setBanner({ kind: 'error', text: BANNER_DOCUMENT_CHANGED });
        onDocumentChanged?.();
        return;
      }
      if (denial?.code === 'AUDIT_UNAVAILABLE') {
        // Fail-closed denial: the export did not happen and nothing left
        // the system. Retryable.
        setBanner({ kind: 'error', text: BANNER_AUDIT_UNAVAILABLE });
        return;
      }
      if (denial?.code === 'DOC_NOT_EXPORTABLE') {
        setBanner({ kind: 'error', text: BANNER_DOC_GENERATING });
        return;
      }

      const message = err instanceof Error ? err.message : BANNER_EXPORT_FAILED;
      setBanner({ kind: 'error', text: message });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40"
      role="presentation"
      data-testid="export-review-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* ── Sticky header ──────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-soft-neutral">
          <h2 id={`${dialogId}-title`} className="text-lg font-semibold text-ink">
            Review before export
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="p-1 rounded-full hover:bg-gray-100 text-muted disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* What's being exported — rendered summary, not raw content */}
          <div
            className="rounded-md border border-soft-neutral bg-white px-4 py-3 text-sm"
            data-testid="content-summary"
          >
            <p className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              What will export
            </p>
            <dl className="space-y-1">
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Document:</dt>
                <dd className="text-ink">{document.title}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Export:</dt>
                <dd className="text-ink">
                  {artifactLabel(artifact)} · {format.toUpperCase()}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Content:</dt>
                <dd className="text-ink" data-testid="content-version">
                  {contentVersionLabel}
                </dd>
              </div>
            </dl>

            {/* Citation status line — replaced by the elevated panel when
                the report is nullish. Codex re-review: also require a present
                `summary` so a malformed/legacy report (citationReport present
                but summary omitted) suppresses this line instead of crashing on
                report.summary.* — such a doc is ungrounded and, for non-citations
                artifacts, already shows the elevated panel; for the citations
                artifact carve-out it now renders nothing rather than throwing. */}
            {!isElevated && report && report.summary && (
              <div className="mt-3 space-y-1" data-testid="citation-status-line">
                <p className="text-xs flex items-center gap-1 text-evergreen">
                  <ShieldCheck size={12} />
                  Citation report included: {report.summary.totalCount}{' '}
                  {report.summary.totalCount === 1 ? 'citation' : 'citations'}{' '}
                  ({report.summary.verifiedCount} verified ·{' '}
                  {report.summary.needsReviewCount} need review ·{' '}
                  {report.summary.unsupportedCount} unsupported)
                </p>
                {report.stale && (
                  <p
                    className="text-xs flex items-center gap-1 text-amber-700"
                    data-testid="citation-stale-line"
                  >
                    <AlertTriangle size={12} />
                    Citations may be outdated. The letter was edited after
                    citations were generated.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Elevated warning notice — letter not backed by citations
              (IMPL-002 §3.1, scope-corrected 2026-06-12 to a calm notice).
              Still tier-distinct for the audit trail. BC-4 pulled the
              zero-citation case (totalCount === 0) forward into this tier out
              of IMPL-003 scope; further escalation tiers remain IMPL-003. */}
          {isElevated && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3"
              role="note"
              data-testid="elevated-warning-panel"
            >
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <AlertTriangle size={16} className="flex-shrink-0" />
                {ELEVATED_WARNING_HEADING}
              </p>
              <p className="mt-2 text-sm text-amber-900 leading-relaxed">
                {ELEVATED_WARNING_BODY}
              </p>
            </div>
          )}

          {/* Review notice — calm, plain; the export button below is the
              single deliberate acknowledgment action. */}
          <div
            className="rounded-md border border-soft-neutral bg-gray-50 px-4 py-3 text-sm text-ink leading-relaxed"
            data-testid="disclosure-copy"
          >
            <span className="font-semibold">{DISCLOSURE_COPY.lead}</span>
            {DISCLOSURE_COPY.rest}
          </div>

          {/* Banners */}
          {banner && banner.kind === 'error' && (
            <div
              role="alert"
              data-testid="error-banner"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}
          {banner && banner.kind === 'reconfirm' && (
            <div
              role="alert"
              data-testid="reconfirm-banner"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}
          {banner && banner.kind === 'success' && (
            <div
              role="status"
              data-testid="success-banner"
              className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-start gap-2"
            >
              <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}
        </div>

        {/* ── Sticky footer ──────────────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-soft-neutral bg-gray-50 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-ink bg-white border border-soft-neutral rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exportDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-evergreen rounded-md hover:bg-deep-forest disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
            data-testid="export-button"
            data-ack-tier={effectiveTier}
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" aria-hidden />}
            {isSubmitting ? 'Recording export…' : CONFIRM_BUTTON_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportReviewModal;
